import React, { useEffect, useState } from 'react';
import { supabaseAdmin } from '../config/supabase';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import {
  normalizeStatusNote,
  notifyReportStatusChange,
} from '../utils/reportStatusNotification';
import './StatusChangeRequests.css';

export default function StatusChangeRequests() {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pendingApproval, setPendingApproval] = useState(null);
  const [approvalNoteMode, setApprovalNoteMode] = useState('choice'); // 'choice' | 'note'
  const [approvalNoteText, setApprovalNoteText] = useState('');

  useEffect(() => {
    loadRequests();
  }, []);

  const loadRequests = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabaseAdmin
        .from('status_change_requests')
        .select(`
          *,
          reports:report_id (
            id,
            title,
            description,
            status,
            report_type
          ),
          requested_by_user:requested_by (
            id,
            name,
            email
          )
        `)
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setRequests(data || []);
    } catch (error) {
      console.error('Error loading requests:', error);
      toast.error('Błąd podczas ładowania próśb');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (requestId, reportId, newStatus, supportMessage = '', adminNote = '') => {
    const supportNotificationNote = normalizeStatusNote(supportMessage);
    const adminNotificationNote = normalizeStatusNote(adminNote);
    const notificationNote = [supportNotificationNote, adminNotificationNote]
      .filter(Boolean)
      .join('\n\n');
    try {
      // Pobierz zgłoszenie z user_id i statusem przed aktualizacją
      const { data: report, error: reportError } = await supabaseAdmin
        .from('reports')
        .select('user_id, title, status')
        .eq('id', reportId)
        .single();

      if (reportError) throw reportError;

      // Guard logic: push wysyłany tylko jeśli status się zmienił
      if (report.status === newStatus) {
        toast.success('Status już jest ustawiony na tę wartość');
        // Nadal aktualizuj prośbę jako zatwierdzoną
        await supabaseAdmin
          .from('status_change_requests')
          .update({
            status: 'approved',
            reviewed_by: user.id,
            reviewed_at: new Date().toISOString(),
          })
          .eq('id', requestId);
        loadRequests();
        return;
      }

      // Update report status
      const { error: updateError } = await supabaseAdmin
        .from('reports')
        .update({
          status: newStatus,
          updated_at: new Date().toISOString(),
          resolved_at: newStatus === 'resolved' ? new Date().toISOString() : null,
        })
        .eq('id', reportId);

      if (updateError) throw updateError;

      // Zapisz historię zmian statusu
      try {
        await supabaseAdmin
          .from('report_status_history')
          .insert({
            report_id: reportId,
            old_status: report.status,
            new_status: newStatus,
            changed_at: new Date().toISOString(),
          });
      } catch (historyError) {
        console.error('Error saving status history:', historyError);
        // Nie przerywamy - status został zaktualizowany
      }

      // Update request status
      const { error: requestError } = await supabaseAdmin
        .from('status_change_requests')
        .update({
          status: 'approved',
          reviewed_by: user.id,
          reviewed_at: new Date().toISOString(),
        })
        .eq('id', requestId);

      if (requestError) throw requestError;

      // Zawsze zapis in-app (REST); Expo OS tylko gdy user ma włączony push
      if (report.user_id) {
        try {
          await notifyReportStatusChange(supabaseAdmin, {
            userId: report.user_id,
            reportId,
            newStatus,
            statusNote: notificationNote,
          });
        } catch (pushError) {
          console.error('Error in status notification flow:', pushError);
          toast.error('Status zmieniony, ale nie udało się zapisać powiadomienia dla użytkownika');
        }
      }

      toast.success('Prośba zatwierdzona i status zgłoszenia zaktualizowany');
      loadRequests();
    } catch (error) {
      console.error('Error approving request:', error);
      toast.error('Błąd podczas zatwierdzania prośby');
    }
  };

  const handleReject = async (requestId, reviewNotes) => {
    try {
      const { error } = await supabaseAdmin
        .from('status_change_requests')
        .update({
          status: 'rejected',
          reviewed_by: user.id,
          reviewed_at: new Date().toISOString(),
          review_notes: reviewNotes,
        })
        .eq('id', requestId);

      if (error) throw error;

      toast.success('Prośba odrzucona');
      loadRequests();
    } catch (error) {
      console.error('Error rejecting request:', error);
      toast.error('Błąd podczas odrzucania prośby');
    }
  };

  const openApprovalModal = (request) => {
    setPendingApproval(request);
    setApprovalNoteMode('choice');
    setApprovalNoteText('');
  };

  const closeApprovalModal = () => {
    setPendingApproval(null);
    setApprovalNoteMode('choice');
    setApprovalNoteText('');
  };

  const confirmApproval = async (adminNote = '') => {
    if (!pendingApproval) return;
    const request = pendingApproval;
    closeApprovalModal();
    await handleApprove(
      request.id,
      request.report_id,
      request.requested_status,
      request.message,
      adminNote,
    );
  };

  const getStatusName = (status) => {
    const names = {
      new: 'Nowe',
      in_progress: 'W trakcie',
      resolved: 'Rozwiązane',
      rejected: 'Odrzucone',
    };
    return names[status] || status;
  };

  if (loading) {
    return <div className="loading">Ładowanie...</div>;
  }

  return (
    <div className="status-change-requests">
      <div className="page-header">
        <h2>Prośby o zmianę statusu zgłoszeń</h2>
        <button className="btn btn-secondary" onClick={loadRequests}>
          Odśwież
        </button>
      </div>

      {requests.length === 0 ? (
        <div className="empty-state">
          <p>Brak oczekujących próśb</p>
        </div>
      ) : (
        <div className="requests-list">
          {requests.map((request) => (
            <div key={request.id} className="request-card">
              <div className="request-header">
                <div>
                  <h3>Zgłoszenie: {request.reports?.report_type || request.reports?.title || 'Brak tytułu'}</h3>
                  <p className="request-meta">
                    Od: {request.requested_by_user?.name || request.requested_by_user?.email} • 
                    {new Date(request.created_at).toLocaleString('pl-PL')}
                  </p>
                </div>
                <div className="status-badges">
                  <span className="badge badge-info">
                    Obecny: {getStatusName(request.current_status)}
                  </span>
                  <span className="badge badge-primary">
                    Prośba: {getStatusName(request.requested_status)}
                  </span>
                </div>
              </div>
              <div className="request-body">
                <p><strong>Wiadomość:</strong></p>
                <p>{request.message}</p>
                {request.image_url && (
                  <div className="request-image">
                    <img src={request.image_url} alt="Zdjęcie rozwiązania" />
                  </div>
                )}
              </div>
              <div className="request-actions">
                <button
                  className="btn btn-success"
                  onClick={() => openApprovalModal(request)}
                >
                  Zatwierdź
                </button>
                <button
                  className="btn btn-danger"
                  onClick={() => {
                    const notes = window.prompt('Podaj powód odrzucenia (opcjonalnie):');
                    if (notes !== null) {
                      handleReject(request.id, notes);
                    }
                  }}
                >
                  Odrzuć
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {pendingApproval && (
        <div className="modal-overlay" onClick={closeApprovalModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '520px' }}>
            <div className="modal-header">
              <h3>Informacja dla użytkownika</h3>
              <button className="modal-close" onClick={closeApprovalModal}>
                ×
              </button>
            </div>

            {approvalNoteMode === 'choice' ? (
              <>
                <p style={{ padding: '16px 0', margin: 0 }}>
                  Możesz zatwierdzić zmianę statusu bez dodatkowej informacji administratora albo dopisać wiadomość,
                  która zostanie dołączona do powiadomienia użytkownika.
                </p>
                {normalizeStatusNote(pendingApproval.message) && (
                  <p style={{ margin: '0 0 16px 0', color: '#666' }}>
                    Wiadomość od Obsługi z prośby zostanie dołączona do powiadomienia.
                  </p>
                )}
                <div className="modal-actions">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={closeApprovalModal}
                  >
                    Anuluj
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => confirmApproval('')}
                  >
                    Zmień status bez informacji
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => setApprovalNoteMode('note')}
                  >
                    Dodaj informację dla użytkownika
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="form-group">
                  <label className="form-label">Informacja dla użytkownika *</label>
                  <textarea
                    className="form-textarea"
                    value={approvalNoteText}
                    onChange={(e) => setApprovalNoteText(e.target.value)}
                    placeholder="Wpisz dodatkową informację administratora..."
                    rows={5}
                    autoFocus
                  />
                </div>
                <div className="modal-actions">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setApprovalNoteMode('choice')}
                  >
                    Wróć
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => {
                      const cleanNote = normalizeStatusNote(approvalNoteText);
                      if (!cleanNote) {
                        toast.error('Wpisz informację dla użytkownika albo wróć i wybierz zmianę bez informacji');
                        return;
                      }
                      confirmApproval(cleanNote);
                    }}
                  >
                    Zmień status i wyślij informację
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
